/**
 * metaPixel.js — Meta Pixel (browser) helper
 *
 * Documentação: https://developers.facebook.com/docs/meta-pixel/reference
 * Deduplicação: https://developers.facebook.com/docs/marketing-api/conversions-api/deduplicate-pixel-and-server-events
 *
 * Regras de deduplicação Meta:
 *  - event_name (Pixel) deve corresponder a event_name (CAPI)
 *  - eventID (Pixel) deve corresponder a event_id (CAPI)
 *  - Janela de deduplicação: 48 horas
 *  - Em caso de empate (< 5 min), o evento do browser tem prioridade
 *
 * Variável de ambiente necessária:
 *   REACT_APP_META_PIXEL_ID  — ID do Pixel (ex: "1234567890")
 */

const PIXEL_ID = process.env.REACT_APP_META_PIXEL_ID;

// ─── Inicialização ────────────────────────────────────────────────────────────

/**
 * Injeta o base code do Meta Pixel no <head> e dispara PageView.
 * Chamado uma vez no App.js.
 * Seguro para SSR/react-snap: verifica window antes de executar.
 */
export function initMetaPixel() {
  if (typeof window === 'undefined' || !PIXEL_ID) return;
  if (window.fbq) return; // já inicializado (hot reload)

  /* eslint-disable */
  // Base code oficial Meta Pixel — não modificar a estrutura
  !function(f,b,e,v,n,t,s){
    if(f.fbq)return;n=f.fbq=function(){n.callMethod?
    n.callMethod.apply(n,arguments):n.queue.push(arguments)};
    if(!f._fbq)f._fbq=n;n.push=n;n.loaded=!0;n.version='2.0';
    n.queue=[];t=b.createElement(e);t.async=!0;
    t.src=v;s=b.getElementsByTagName(e)[0];
    s.parentNode.insertBefore(t,s)
  }(window,document,'script','https://connect.facebook.net/en_US/fbevents.js');
  /* eslint-enable */

  window.fbq('init', PIXEL_ID);
  window.fbq('track', 'PageView');
}

// ─── Helpers internos ─────────────────────────────────────────────────────────

/**
 * Gera um event_id único para deduplicação Pixel ↔ CAPI.
 * Formato: {eventName}_{orderId ou timestamp}
 * O mesmo ID deve ser enviado no campo event_id da CAPI.
 */
export function generateEventId(eventName, orderId = null) {
  const suffix = orderId || `${Date.now()}_${Math.random().toString(36).slice(2, 8)}`;
  return `${eventName}_${suffix}`;
}

/**
 * Wrapper seguro para fbq — não lança erro se Pixel não estiver carregado.
 */
function fbq(type, eventName, params = {}, options = {}) {
  if (typeof window === 'undefined' || !window.fbq || !PIXEL_ID) return;
  window.fbq(type, eventName, params, options);
}

// ─── Eventos padrão ───────────────────────────────────────────────────────────

/**
 * ViewContent — disparado na página de produto.
 * Ref: https://developers.facebook.com/docs/meta-pixel/reference#standard-events
 *
 * @param {object} params
 * @param {string} params.content_ids  — array com IDs dos produtos
 * @param {string} params.content_name — nome do produto
 * @param {number} params.value        — preço do produto
 * @param {string} params.currency     — moeda (default: 'BRL')
 * @param {string} [eventId]           — event_id para deduplicação (opcional aqui, sem CAPI par)
 */
export function pixelViewContent({ content_ids, content_name, value, currency = 'BRL' }, eventId) {
  fbq(
    'track',
    'ViewContent',
    { content_ids, content_name, content_type: 'product', value, currency },
    eventId ? { eventID: eventId } : {}
  );
}

/**
 * AddToCart — disparado ao adicionar produto ao carrinho.
 *
 * @param {object} params
 * @param {string[]} params.content_ids  — IDs dos produtos
 * @param {string}   params.content_name — nome do produto
 * @param {number}   params.value        — valor total (preço × qtd)
 * @param {string}   params.currency     — moeda (default: 'BRL')
 * @param {string}   [eventId]           — event_id para deduplicação
 */
export function pixelAddToCart({ content_ids, content_name, value, currency = 'BRL' }, eventId) {
  fbq(
    'track',
    'AddToCart',
    { content_ids, content_name, content_type: 'product', value, currency },
    eventId ? { eventID: eventId } : {}
  );
}

/**
 * InitiateCheckout — disparado ao iniciar o checkout.
 *
 * @param {object} params
 * @param {number}   params.value    — valor total do carrinho
 * @param {string}   params.currency — moeda (default: 'BRL')
 * @param {number}   params.num_items — número de itens
 * @param {string[]} params.content_ids — IDs dos produtos
 * @param {string}   [eventId]       — event_id para deduplicação
 */
export function pixelInitiateCheckout({ value, currency = 'BRL', num_items, content_ids }, eventId) {
  fbq(
    'track',
    'InitiateCheckout',
    { value, currency, num_items, content_ids },
    eventId ? { eventID: eventId } : {}
  );
}

/**
 * Purchase — disparado na confirmação do pedido.
 * IMPORTANTE: o eventId aqui DEVE ser o mesmo enviado para a CAPI
 * para garantir deduplicação correta.
 *
 * @param {object} params
 * @param {number}   params.value       — valor total pago
 * @param {string}   params.currency    — moeda (default: 'BRL')
 * @param {string[]} params.content_ids — IDs dos produtos
 * @param {string}   params.order_id    — ID do pedido (paymentId)
 * @param {string}   eventId            — event_id = generateEventId('Purchase', order_id)
 */
export function pixelPurchase({ value, currency = 'BRL', content_ids, order_id }, eventId) {
  fbq(
    'track',
    'Purchase',
    { value, currency, content_ids, content_type: 'product', order_id },
    { eventID: eventId } // obrigatório para deduplicação com CAPI
  );
}
